import { Slider, Tooltip } from 'antd';
import { Collapse } from 'antd';
import { Dispatch, useCallback, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { QuestionMarkSvg } from '../../../../assets/icons';
import {
  allPromptOutputsSelector,
  selectedSlideIdSelector,
  TemplateAction,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useSinglePromptTestCase from '../../../../hooks/useSinglePromptTestCase';
import {
  useAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  AIResourceTypes,
  ById,
  Prompt,
  PromptDependency,
  PromptInput,
  PromptModels,
  promptModels,
  PromptSingleTestCaseResultType,
  ResourceType,
} from '../../../../services/backendService/types';
import { reconnectPromptDependantElements } from '../../../../utils/aiGeneratorUtils';
import { Input } from '../../../_atoms/_Input';
import Select from '../../../_atoms/_Select/Select';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import {
  CuripodDecendant,
  isCuripodElement,
  isCuripodPromptInputElement,
  isCuripodPromptOutputElement,
} from '../../../SlideComponents/slateUtil';
import { SlideElement } from '../../../SlideComponents/slide.types';
import { createTextElement } from '../../../SlideComponents/slideUtil';
import { AIConnectorConfirmModal } from './PromptAIConnector';
import PromptOutputBuilder from './PromptOutputBuilder';
import { PromptTextEditor } from './PromptTextEditor';

const PromptBuilder = ({
  prompt,
  localTemplateDispatch,
  promptInputs,
  setShowEditInputs,
}: {
  prompt: Prompt;
  promptInputs?: PromptInput[];
  localTemplateDispatch: Dispatch<TemplateAction>;
  setShowEditInputs: (show: boolean) => void;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const lessonDispatch = useDesignerDataContext(c => c.dispatch);
  const presentationId = useDesignerDataContext(c => c.state?.localTemplate?.id);
  const selectedSlideId = useDesignerDataContext(selectedSlideIdSelector);
  const onAddElements = useDesignerDataContext(context => context.handleElementsAdd);
  const slides = useDesignerDataContext(c => c.state.localTemplate?.content.slides || []);

  const promptDependentInputs = useDesignerUIContext(c => c.state.promptDependentInputs);

  const showTabPaneFullWidth = useDesignerUIContext(c => c.state.showTabPaneFullWidth);
  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );

  const selectedElement = useMemo(() => {
    if (selectedElements?.length !== 1) return null;
    return selectedElements[0];
  }, [selectedElements]);

  const {
    generate,
    isLoading,
    reset: resetSingleTestCase,
    isSuccess: isSinglePromptTestCaseSuccess,
  } = useSinglePromptTestCase(prompt.id, presentationId);

  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const { availablePromptDependencies } = useAvailablePromptDependencies({
    promptId: prompt.id,
    promptInputs,
    promptOutputs,
  });

  const systemInstructionText = useMemo(() => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    return systemMessage?.content;
  }, [prompt]);

  const handleSystemInstructionChange = (val: string) => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    if (systemMessage) {
      localTemplateDispatch({
        type: TemplateStateActions.editPrompt,
        prompt: {
          ...prompt,
          messages: prompt.messages.map(message =>
            message.role === 'system' ? { ...systemMessage, content: val } : message,
          ),
        },
      });
    }
  };

  const handleChatHistoryContentChanged = (val: string, index: number) => {
    const newMessage = { ...prompt.messages[index] };
    if (newMessage) {
      localTemplateDispatch({
        type: TemplateStateActions.editPrompt,
        prompt: {
          ...prompt,
          messages: prompt.messages.map((message, i) =>
            i === index ? { ...newMessage, content: val } : message,
          ),
        },
      });
    }
  };

  const handleChangeTemperature = (e: number) => {
    localTemplateDispatch({
      type: TemplateStateActions.editPrompt,
      prompt: {
        ...prompt,
        temperature: e,
      },
    });
  };

  const dependencies = useMemo(() => findDependencies(prompt), [prompt]);

  const onAddAiText = (promptSourceId: string, textValue?: string) => {
    if (!textValue) return;

    const textElement = createTextElement({
      textValue,
      fontSize: 40,
      textColor: '#FFFFFF',
      x: 100,
      y: 100,
    });
    const aiTextElement: SlideElement = {
      ...textElement,
      promptSourceId: promptSourceId,
      type: 'generatorText',
    };
    onAddElements([aiTextElement], true);
  };

  const updatePromptDependentInputs = useCallback(
    (payload: { id: string; value: string; type: PromptSingleTestCaseResultType }) => {
      dispatch({
        type: DesignerUIStateActions.updatePromptDependentInputs,
        payload,
      });
    },
    [dispatch],
  );

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  useEffect(() => {
    if (!isSinglePromptTestCaseSuccess) {
      return; // Don't run
    }

    // Whenever a successfull test case is generated, we update all the slides with the latest output
    if (selectedSlideId) {
      const { slides: newSlides } = reconnectPromptDependantElements({
        promptResultData: promptDependentInputs || {},
        slides,
      });

      lessonDispatch({
        type: TemplateStateActions.allSlidesChanged,
        slides: newSlides,
        selectedSlideId: selectedSlideId,
      });
      resetSingleTestCase(); // make sure we reset the state to this doesn't trigger multiple times
    }
  }, [
    isSinglePromptTestCaseSuccess,
    slides,
    promptDependentInputs,
    lessonDispatch,
    selectedSlideId,
    resetSingleTestCase,
  ]);

  const handleResourceChanged = (resource: string, model: string) => {
    localTemplateDispatch({
      type: TemplateStateActions.editPrompt,
      prompt: {
        ...prompt,
        promptOutput: {
          ...prompt.promptOutput,
          resource: {
            type: resource as ResourceType,
            model: model as PromptModels,
          },
        },
      },
    });
  };
  const handleGenerateSinglePromptTestCase = useCallback(() => {
    if (!presentationId) {
      return;
    }
    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });
    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );
    generate({
      presentationId,
      promptId: prompt.id,
      userInputFormData: inputData.map(key => ({
        id: key,
        type: 'string',
        value: testCaseInputs[key],
      })),
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  }, [
    generate,
    presentationId,
    prompt.id,
    promptInputs,
    visiblePromptDependentInputValues,
    dependencies,
  ]);

  const runTestcaseDisabled = useMemo(() => {
    //check if there is any input that has not been filled
    return visibleInputs.some(dependency => {
      return !visiblePromptDependentInputValues?.[dependency.id];
    });
  }, [visiblePromptDependentInputValues, visibleInputs]);

  return (
    <>
      <AIConnectorConfirmModal promptResult={promptDependentInputs} />

      <div
        style={{
          padding: showTabPaneFullWidth ? '0px' : '0px 0px 20px 0px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          height: '100%',
          alignContent: 'start',
          gridTemplateRows: showTabPaneFullWidth ? 'auto 1fr' : 'fit-content(100%) 1fr',
        }}
      >
        <div
          style={{
            overflowY: showTabPaneFullWidth ? 'hidden' : 'auto',
            height: '100%',
            borderRadius: 10,
            overflowX: 'hidden',
            display: 'grid',
            gridTemplateColumns: showTabPaneFullWidth ? '1fr 1fr' : '1fr',
          }}
        >
          <LeftSideContainer $showTabPaneFullWidth={showTabPaneFullWidth}>
            <div
              style={{
                overflowY: 'auto',
                height: '100%',
              }}
            >
              <div>
                <p style={{ marginBottom: 5, fontSize: 16 }}>
                  System context instruction
                </p>
                <div style={{ borderRadius: 10, overflow: 'hidden' }}>
                  <PromptTextEditor
                    style={{ borderRadius: 10 }}
                    availablePromptDependencies={availablePromptDependencies}
                    value={systemInstructionText}
                    onChange={handleSystemInstructionChange}
                  />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex' }}>
                  <p style={{ marginBottom: 5, flex: 1, fontSize: 16 }}>
                    Prompt instruction
                  </p>
                  <Button
                    type="ghost"
                    styles={{
                      textDecoration: 'underline',
                      border: 'none',
                      padding: '2px 10px',
                      marginRight: 30,
                      marginBottom: 5,
                    }}
                    onClick={() => setShowEditInputs(true)}
                  >
                    Edit inputs
                  </Button>
                </div>
                <MessageContainer>
                  {prompt.messages.map((message, i) =>
                    i === 0 ? null : (
                      <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ flex: 1, borderRadius: 10, overflow: 'hidden' }}>
                          <PromptTextEditor
                            key={`${i}`}
                            value={message.content}
                            availablePromptDependencies={availablePromptDependencies}
                            onChange={val => handleChatHistoryContentChanged(val, i)}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </MessageContainer>
              </div>
              <AdvancedOptionsContainer>
                <Collapse ghost>
                  <Collapse.Panel
                    header="Advanced options"
                    key="1"
                    style={{ padding: '0' }}
                  >
                    <Select
                      label="Model"
                      value={prompt.promptOutput.resource.model}
                      onSelect={val => {
                        if (val === promptModels.gpt35Turbo) {
                          handleResourceChanged(
                            AIResourceTypes.openai,
                            promptModels.gpt35Turbo,
                          );
                        }
                        if (val === promptModels.gpt4) {
                          handleResourceChanged(
                            AIResourceTypes.openai,
                            promptModels.gpt4,
                          );
                        }
                        if (val === promptModels.gpt4Turbo) {
                          handleResourceChanged(
                            AIResourceTypes.openai,
                            promptModels.gpt4Turbo,
                          );
                        }
                        if (val === promptModels.gpt4o) {
                          handleResourceChanged(
                            AIResourceTypes.openai,
                            promptModels.gpt4o,
                          );
                        }
                        if (val === promptModels.gpt4omini) {
                          handleResourceChanged(
                            AIResourceTypes.openai,
                            promptModels.gpt4omini,
                          );
                        }
                      }}
                      options={[
                        {
                          displayName: 'GPT-4',
                          value: promptModels.gpt4,
                        },
                        {
                          displayName: 'GPT-4-turbo (beta)',
                          value: promptModels.gpt4Turbo,
                        },
                        {
                          displayName: 'GPT-4o',
                          value: promptModels.gpt4o,
                        },
                        {
                          displayName: 'GPT-4o-mini',
                          value: promptModels.gpt4omini,
                        },
                      ]}
                    />
                    <div style={{ paddingTop: '20px', display: 'flex' }}>
                      Creativity: {prompt.temperature || 0.7}
                      <Tooltip
                        mouseEnterDelay={0.5}
                        title={
                          "High creativity means that the prompt's output will be more creative and less likely to be similar with each lesson generation, but also more unpredictable and more likely to be incorrect. A low temperature means the output will be more consistent, but at the expense of creativity, and it might feel stale"
                        }
                      >
                        <IconWrapper
                          width="15px"
                          height="15px"
                          style={{ marginLeft: '10px' }}
                        >
                          <QuestionMarkSvg />
                        </IconWrapper>
                      </Tooltip>
                    </div>
                    <Slider
                      min={0}
                      max={2}
                      step={0.01}
                      value={prompt.temperature || 0.7}
                      onChange={e => handleChangeTemperature(e)}
                    />
                  </Collapse.Panel>
                </Collapse>
              </AdvancedOptionsContainer>
            </div>
            {showTabPaneFullWidth && (
              <FixedContainer $showTabPaneFullWidth={showTabPaneFullWidth}>
                <Collapse ghost defaultActiveKey={['1']}>
                  <Collapse.Panel
                    header={
                      <p style={{ marginBottom: 5, flex: 1, fontSize: 16 }}>
                        Test case inputs
                      </p>
                    }
                    key="1"
                    style={{ padding: '0' }}
                  >
                    <PromptOutputContainer>
                      {visibleInputs.map(dependency => {
                        const dependencyId = dependency?.id;
                        if (!dependencyId) return null;
                        if (dependency.formType === 'dropdown') {
                          return (
                            <Select
                              label={dependency?.label}
                              placeholder={dependency?.defaultValue || 'Select value'}
                              onSelect={val => {
                                updatePromptDependentInputs({
                                  id: dependencyId,
                                  value: val as string,
                                  type:
                                    promptDependentInputs?.[dependencyId]?.type ||
                                    dependency.formType,
                                });
                              }}
                              value={promptDependentInputs?.[dependencyId]?.value}
                              options={dependency?.constraints?.options || []}
                            ></Select>
                          );
                        }

                        if (dependency.formType === 'longText') {
                          return (
                            <div key={dependencyId}>
                              <TextArea
                                label={dependency?.label}
                                value={promptDependentInputs?.[dependencyId]?.value}
                                placeholder={
                                  dependency?.defaultValue ||
                                  promptDependentInputs?.[dependencyId]?.value ||
                                  'Add a value'
                                }
                                style={{ width: '100%' }}
                                maxLength={dependency.constraints?.maxLength}
                                symbolCount={true}
                                onChange={e =>
                                  updatePromptDependentInputs({
                                    id: dependencyId,
                                    value: e.target.value as string,
                                    type:
                                      promptDependentInputs?.[dependencyId]?.type ||
                                      dependency.formType,
                                  })
                                }
                              />
                            </div>
                          );
                        }
                        return (
                          <div key={dependencyId}>
                            <Input
                              label={dependency?.label}
                              value={promptDependentInputs?.[dependencyId]?.value}
                              placeholder={
                                dependency?.defaultValue ||
                                promptDependentInputs?.[dependencyId]?.value ||
                                'Add a value'
                              }
                              onChange={e => {
                                updatePromptDependentInputs({
                                  id: dependencyId,
                                  value: e.target.value as string,
                                  type:
                                    promptDependentInputs?.[dependencyId]?.type ||
                                    dependency.formType,
                                });
                              }}
                            />
                          </div>
                        );
                      })}
                    </PromptOutputContainer>
                  </Collapse.Panel>
                </Collapse>
                <div style={{ display: 'flex', marginTop: 10 }}>
                  <Button
                    loading={isLoading}
                    onClick={() => handleGenerateSinglePromptTestCase()}
                    disabled={runTestcaseDisabled}
                  >
                    Run test case
                  </Button>
                </div>
              </FixedContainer>
            )}
          </LeftSideContainer>
          <RightSideContainer $showTabPaneFullWidth={showTabPaneFullWidth}>
            <PromptOutputBuilder
              prompt={prompt}
              onOutputChanged={resetSingleTestCase}
              promptDependentInputs={promptDependentInputs}
              onAddAiText={onAddAiText}
              selectedElement={selectedElement}
            />
          </RightSideContainer>
        </div>
        {!showTabPaneFullWidth && (
          <FixedContainer $showTabPaneFullWidth={showTabPaneFullWidth}>
            <Collapse ghost>
              <Collapse.Panel
                header={
                  <p style={{ marginBottom: 5, flex: 1, fontSize: 16 }}>
                    Test case inputs
                  </p>
                }
                key="1"
                style={{ padding: '0' }}
              >
                <PromptOutputContainer>
                  {visibleInputs.map(dependency => {
                    const dependencyId = dependency?.id;
                    if (!dependencyId) return null;
                    if (dependency.formType === 'dropdown') {
                      return (
                        <Select
                          label={dependency?.label}
                          placeholder={dependency?.defaultValue || 'Select value'}
                          onSelect={val => {
                            updatePromptDependentInputs({
                              id: dependencyId,
                              value: val as string,
                              type:
                                promptDependentInputs?.[dependencyId]?.type ||
                                dependency.formType,
                            });
                          }}
                          value={promptDependentInputs?.[dependencyId]?.value}
                          options={dependency?.constraints?.options || []}
                        ></Select>
                      );
                    }

                    if (dependency.formType === 'longText') {
                      return (
                        <div key={dependencyId}>
                          <TextArea
                            label={dependency?.label}
                            value={promptDependentInputs?.[dependencyId]?.value}
                            placeholder={
                              dependency?.defaultValue ||
                              promptDependentInputs?.[dependencyId]?.value ||
                              'Add a value'
                            }
                            style={{ width: '100%' }}
                            maxLength={dependency.constraints?.maxLength}
                            symbolCount={true}
                            onChange={e =>
                              updatePromptDependentInputs({
                                id: dependencyId,
                                value: e.target.value as string,
                                type:
                                  promptDependentInputs?.[dependencyId]?.type ||
                                  dependency.formType,
                              })
                            }
                          />
                        </div>
                      );
                    }
                    return (
                      <div key={dependencyId}>
                        <Input
                          label={dependency?.label}
                          value={promptDependentInputs?.[dependencyId]?.value}
                          placeholder={
                            dependency?.defaultValue ||
                            promptDependentInputs?.[dependencyId]?.value ||
                            'Add a value'
                          }
                          onChange={e => {
                            updatePromptDependentInputs({
                              id: dependencyId,
                              value: e.target.value as string,
                              type:
                                promptDependentInputs?.[dependencyId]?.type ||
                                dependency.formType,
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </PromptOutputContainer>
              </Collapse.Panel>
            </Collapse>
            <div style={{ display: 'flex', marginTop: 10 }}>
              <Button
                loading={isLoading}
                onClick={() => handleGenerateSinglePromptTestCase()}
                disabled={runTestcaseDisabled}
              >
                Run test case
              </Button>
            </div>
          </FixedContainer>
        )}
      </div>
    </>
  );
};

export const findDependencies = (prompt: Prompt): PromptDependency[] => {
  /** This function is a replica from backend that extracts all the dependencies from messages, updated here should be reflected in the backend. and vice versa */
  const dependencies: ById<PromptDependency> = {};

  prompt.messages.forEach(message => {
    if (!message.content) return;
    const blocks = JSON.parse(message.content) as CuripodDecendant[];

    blocks.forEach(block => {
      if (isCuripodElement(block)) {
        if (block.type === 'paragraph') {
          const children = block.children as CuripodDecendant[];

          children.forEach(child => {
            if (isCuripodPromptInputElement(child)) {
              dependencies[child.inputId] = {
                dependencyId: child.inputId,
                type: 'formInput',
              };
            }

            if (isCuripodPromptOutputElement(child)) {
              dependencies[child.outputId] = {
                dependencyId: child.outputId,
                type: 'promptOutput',
              };
            }
          });
        }
      }
    });
  });

  return Object.values(dependencies);
};

const MessageContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const AdvancedOptionsContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    padding: 0;
  }
  .ant-collapse-header {
    font-weight: bold;
  }
`;

const LeftSideContainer = styled.div<{ $showTabPaneFullWidth: boolean }>`
  height: fit-content;

  ${props =>
    props.$showTabPaneFullWidth &&
    css`
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
      overflow-y: auto;
      grid-template-rows: auto 1fr;
      align-content: start;
      height: 100%;
      padding: 0px 10px 0px 10px;
    `}
`;

const RightSideContainer = styled.div<{ $showTabPaneFullWidth: boolean }>`
  height: fit-content;

  ${props =>
    props.$showTabPaneFullWidth &&
    css`
      width: 100%;
      display: grid;
      grid-template-columns: 1fr;
      overflow-y: auto;
      height: 100%;
      padding: 0px 10px 0px 10px;
    `}
`;

const FixedContainer = styled.div<{
  $showTabPaneFullWidth: boolean;
}>`
  background-color: white;
  width: 100%;
  padding: 10px;

  .ant-collapse-header {
    padding-bottom: 0 !important;
    padding-left: 0 !important;
  }
  .ant-collapse-content-box {
    padding-top: 0 !important;
  }
`;
const PromptOutputContainer = styled(MessageContainer)``;

export default PromptBuilder;
